import React from 'react'
import { UserService } from '@duck/api-definitions'
import { CopyableText } from '@duck/ui'
import { useTar, type TranslationKey } from '@/modules/i18n'
import { RelativeDateTime } from '@/components/datetime/RelativeDateTime'
import { getDisplayedStatusDate } from '@/hooks/TableSettings/peopleTable.utils'

interface PersonDetailsPanelProps {
  person: UserService.Person
  /** Linked user account's createdAt — the real "joined" date. Absent for added/invited people. */
  joinedAt?: string
}

export const PersonDetailsPanel: React.FC<PersonDetailsPanelProps> = ({ person, joinedAt }) => {
  const { t } = useTar()
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim() || person.email || person.id
  const initials = `${person.firstName?.charAt(0) || ''}${person.lastName?.charAt(0) || ''}`.toUpperCase()
  const statusWord = t(`partner.onboarding_${person.onboardingStatus}` as TranslationKey)
  const statusIso = getDisplayedStatusDate(person, joinedAt)

  return (
    <div className="px-4 py-3 ps-4 lg:ps-12">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{initials}</span>
        <div className="min-w-0 space-y-1 text-xs">
          <p className="text-sm font-semibold text-foreground">{name}</p>
          {person.email && (
            <div className="flex items-center gap-1" dir="ltr">
              <span className="text-muted-foreground">{t('common.email')}:</span>
              <CopyableText value={person.email}><span className="font-mono break-all">{person.email}</span></CopyableText>
            </div>
          )}
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">{statusWord}{statusIso ? ':' : ''}</span>
            {statusIso && <RelativeDateTime isoDateString={statusIso} className="text-foreground" nativeTooltip />}
          </div>
        </div>
      </div>
    </div>
  )
}
